import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getFellow, updateProfile } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
  EnvelopeIcon, PhoneIcon, LinkIcon, AcademicCapIcon, PencilIcon,
  CheckIcon, XMarkIcon, MapPinIcon, CalendarDaysIcon,
} from '@heroicons/react/24/outline';

const PROJECT_COLORS = {
  'Y-Check':      'bg-blue-900/60 text-blue-300',
  'Neotree':      'bg-green-900/60 text-green-300',
  'KOSHESAI':     'bg-purple-900/60 text-purple-300',
  'PROMISE':      'bg-amber-900/60 text-amber-300',
  'Fractures E3': 'bg-red-900/60 text-red-300',
  'Helen Jack':   'bg-teal-900/60 text-teal-300',
};

function Field({ label, field, type = 'text', editing, canEdit, form, setForm, profile }) {
  return (
    <div>
      <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">{label}</div>
      {editing && canEdit ? (
        <input
          type={type}
          className="input text-sm"
          value={form[field] || ''}
          onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
        />
      ) : (
        <div className="text-sm text-gray-200">
          {type === 'date' && profile[field]
            ? format(new Date(profile[field]), 'dd MMM yyyy')
            : (profile[field] || <span className="text-gray-600">—</span>)
          }
        </div>
      )}
    </div>
  );
}

function SelectField({ label, field, options, editing, canEdit, form, setForm, profile }) {
  return (
    <div>
      <div className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">{label}</div>
      {editing && canEdit ? (
        <select
          className="input text-sm"
          value={form[field] || ''}
          onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
        >
          <option value="">— Select —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <div className="text-sm text-gray-200">{profile[field] || <span className="text-gray-600">—</span>}</div>
      )}
    </div>
  );
}

export default function FellowProfilePage() {
  const { id } = useParams();
  const { user, isAdmin } = useAuth();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const { data, isLoading } = useQuery({
    queryKey: ['fellow', id],
    queryFn: () => getFellow(id),
    onSuccess: (res) => setForm(res.data),
  });

  const profile = data?.data;

  const mutation = useMutation({
    mutationFn: (payload) => updateProfile(id, payload),
    onSuccess: () => {
      qc.invalidateQueries(['fellow', id]);
      toast.success('Profile updated');
      setEditing(false);
    },
    onError: () => toast.error('Failed to update profile'),
  });

  const canEdit = user?.id === Number(id) || isAdmin;
  const fieldProps = { editing, canEdit, form, setForm, profile: profile || {} };

  if (isLoading) return <div className="text-gray-400 text-sm">Loading profile...</div>;
  if (!profile) return <div className="text-gray-400 text-sm">Fellow not found.</div>;

  return (
    <div className="max-w-3xl space-y-5">
      {/* Header card */}
      <div className="card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary-600/30 flex items-center justify-center text-primary-400 text-2xl font-bold flex-shrink-0">
              {profile.user?.full_name?.[0]?.toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{profile.user?.full_name}</h1>
              <div className="text-gray-500 text-sm">{profile.degree}{profile.university ? ` · ${profile.university}` : ''}</div>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <span className={`badge ${profile.current_status === 'Active' ? 'bg-green-900/50 text-green-300' : 'bg-gray-800 text-gray-400'}`}>
                  {profile.current_status || 'Active'}
                </span>
                {profile.thru_project && (
                  <span className={`badge ${PROJECT_COLORS[profile.thru_project] || 'bg-gray-800 text-gray-400'}`}>
                    {profile.thru_project}
                  </span>
                )}
                {profile.location && (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <MapPinIcon className="w-3.5 h-3.5" /> {profile.location}
                  </span>
                )}
              </div>
            </div>
          </div>
          {canEdit && !editing && (
            <button
              onClick={() => {
                setForm({
                  ...profile,
                  start_date: profile.start_date ? profile.start_date.split('T')[0] : '',
                  expected_completion: profile.expected_completion ? profile.expected_completion.split('T')[0] : '',
                });
                setEditing(true);
              }}
              className="btn-ghost flex items-center gap-2"
            >
              <PencilIcon className="w-4 h-4" /> Edit
            </button>
          )}
          {editing && (
            <div className="flex gap-2">
              <button onClick={() => mutation.mutate(form)} className="btn-primary flex items-center gap-1">
                <CheckIcon className="w-4 h-4" /> Save
              </button>
              <button onClick={() => setEditing(false)} className="btn-ghost flex items-center gap-1">
                <XMarkIcon className="w-4 h-4" /> Cancel
              </button>
            </div>
          )}
        </div>

        {(profile.bio || editing) && (
          <div className="mt-4 border-t border-gray-800 pt-4">
            {editing && canEdit ? (
              <>
                <label className="text-xs text-gray-500 uppercase font-medium block mb-1">Bio</label>
                <textarea
                  className="input resize-none text-sm"
                  rows={3}
                  value={form.bio || ''}
                  onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                />
              </>
            ) : (
              <p className="text-sm text-gray-400">{profile.bio}</p>
            )}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Academic */}
        <div className="card space-y-4">
          <h2 className="font-semibold text-white text-sm border-b border-gray-800 pb-2 flex items-center gap-2">
            <AcademicCapIcon className="w-4 h-4 text-primary-400" /> Academic Details
          </h2>
          <Field label="University" field="university" {...fieldProps} />
          <Field label="Degree / Programme" field="degree" {...fieldProps} />
          <Field label="Supervisor" field="supervisor" {...fieldProps} />
          <Field label="Project Title" field="project_title" {...fieldProps} />
          <Field label="Research Interests" field="research_interests" {...fieldProps} />
          <SelectField
            label="Current Status"
            field="current_status"
            options={['Active', 'Completed', 'On Leave', 'Withdrawn']}
            {...fieldProps}
          />
        </div>

        {/* THRU & Dates */}
        <div className="card space-y-4">
          <h2 className="font-semibold text-white text-sm border-b border-gray-800 pb-2 flex items-center gap-2">
            <CalendarDaysIcon className="w-4 h-4 text-primary-400" /> Fellowship Details
          </h2>
          <SelectField
            label="THRU ZIM Project"
            field="thru_project"
            options={['Y-Check', 'Neotree', 'KOSHESAI', 'PROMISE', 'Fractures E3', 'Helen Jack']}
            {...fieldProps}
          />
          <Field label="Project Type" field="project_type" {...fieldProps} />
          <Field label="Location" field="location" {...fieldProps} />
          <Field label="MSc Start Date" field="start_date" type="date" {...fieldProps} />
          <Field label="Expected Completion" field="expected_completion" type="date" {...fieldProps} />
        </div>
      </div>

      {/* Contact */}
      <div className="card space-y-4">
        <h2 className="font-semibold text-white text-sm border-b border-gray-800 pb-2">Contact & Links</h2>
        <div className="flex items-center gap-2 text-sm">
          <EnvelopeIcon className="w-4 h-4 text-gray-500" />
          <span className="text-gray-300">{profile.user?.email}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Phone" field="phone" {...fieldProps} />
          <Field label="LinkedIn URL" field="linkedin_url" {...fieldProps} />
        </div>
        {profile.linkedin_url && !editing && (
          <a href={profile.linkedin_url} target="_blank" rel="noreferrer" className="text-xs text-primary-400 hover:underline flex items-center gap-1">
            <LinkIcon className="w-3 h-3" /> View LinkedIn
          </a>
        )}
      </div>
    </div>
  );
}
